<template>
	<v-tooltip v-if="tooltip.text" v-bind="tooltipAttrs">
		<template #activator="{ props: tooltipProps }">
			<div>
				<v-btn v-bind="{ ...$attrs, ...tooltipProps, ...buttonAttrs }">
					<v-icon :start="!notext" v-bind="iconAttrs">
						{{ te(computedIcon) ? translate(computedIcon) : computedIcon }}
					</v-icon>
					<span v-if="!notext">{{ translate(computedText) }}</span>
					<v-icon v-if="iconRight" v-bind="iconAttrs" end>
						{{ iconRight }}
					</v-icon>
				</v-btn>
			</div>
		</template>
		<span>{{ translate(tooltip.text) }}</span>
	</v-tooltip>
	<v-btn v-else v-bind="{ ...$attrs, ...buttonAttrs }">
		<v-icon :start="!notext" v-bind="iconAttrs">
			{{ te(computedIcon) ? translate(computedIcon) : computedIcon }}
		</v-icon>
		<span v-if="!notext && $vuetify.display.mdAndUp">{{
			translate(computedText)
		}}</span>
		<v-icon v-if="iconRight" v-bind="iconAttrs" end>
			{{ iconRight }}
		</v-icon>
	</v-btn>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useDisplay } from 'vuetify'
import { typeMapping } from '@/constants/buttonIconTypes.ts'
import { useTranslate } from 'composables/useTranslate.ts'

interface TooltipConfig {
	text?: string
	top?: boolean
	left?: boolean
	bottom?: boolean
	right?: boolean
}

interface ButtonIconProps {
	classes?: string[]
	color?: string
	icon?: string
	iconRight?: string
	iconSize?: 'x-small' | 'small' | 'default' | 'large' | 'x-large'
	notext?: boolean
	responsive?: boolean
	submit?: boolean
	text?: string
	tooltip?: TooltipConfig
	type?: 'add' | 'save' | 'cancel' | 'close' | 'delete' | 'update' | null
	wrapper?: boolean
	variant?: 'elevated' | 'flat' | 'tonal' | 'outlined' | 'text' | 'plain'
	dataTest?: string | undefined
}

const props = withDefaults(defineProps<ButtonIconProps>(), {
	classes: () => [],
	color: 'warning',
	icon: 'fal fa-question',
	iconRight: '',
	iconSize: 'x-small',
	notext: false,
	responsive: true,
	submit: false,
	text: 'Text',
	tooltip: () => ({
		text: undefined,
		top: true,
	}),
	type: null,
	wrapper: true,
	variant: 'elevated',
	dataTest: undefined,
})

const { smAndDown, mdAndUp } = useDisplay()

const { translate, te } = useTranslate()

const computedDataTest = computed<string | undefined>(() => {
	if (props.dataTest) {
		return props.dataTest
	}
	if (props.type && typeMapping[props.type]) {
		return typeMapping[props.type].dataTest
	}
	return undefined
})

const computedColor = computed<string>(() => {
	if (props.type && typeMapping[props.type]) {
		return typeMapping[props.type].color
	}
	return props.color
})

const computedIcon = computed<string>(() => {
	if (props.type && typeMapping[props.type]) {
		return typeMapping[props.type].icon
	}
	return props.icon
})

const computedText = computed<string>(() => {
	if (props.type && typeMapping[props.type]) {
		return typeMapping[props.type].text
	}
	return props.text
})

const buttonAttrs = computed(() => {
	const classes = [...props.classes]

	if (smAndDown.value && props.responsive) {
		classes.push('mx-2')
	}

	const attributes = {
		variant: props.notext ? 'text' : props.variant,
		density: props.notext ? 'compact' : 'default',
		color: computedColor.value,
		icon: props.notext,
		type: props.submit ? 'submit' : 'button',
		class: classes,
	}

	if (computedDataTest.value) {
		attributes['data-test'] = computedDataTest.value
	}

	return attributes
})

const iconAttrs = computed(() => {
	const classes = ['pl-2']

	if (smAndDown.value && props.responsive) {
		classes.push('subheading')
	}

	const attrs: Record<string, any> = {
		class: classes,
		dark: true,
	}

	// Handle icon size
	if (props.iconSize !== 'default') {
		attrs.size = props.iconSize
	}

	// Handle left positioning for larger screens
	if ((mdAndUp.value || !props.responsive) && !props.notext) {
		attrs.left = true
	}

	return attrs
})

const tooltipAttrs = computed(() => {
	const defaultAttrs = {
		top: !props.tooltip.left && !props.tooltip.bottom && !props.tooltip.right,
	}

	return { ...defaultAttrs, ...props.tooltip }
})
</script>

<style lang="scss" scoped>
.v-btn--disabled {
  background-color: rgba(var(--v-theme-on-surface), 0.2);
}

span {
  letter-spacing: 0;
  text-transform: none;
}
</style>
